import React, { useMemo, useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";

import TopBar from "./components/TopBar";
import SideNav, { SIDENAV_DEFAULT_WIDTH } from "./components/SideNav";
import ErrorBoundary from "./components/ErrorBoundary";

import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ToastProvider } from "./ui/Toaster";
import { CommandPalette, useOpenCommandPalette } from "./ui/CommandPalette";
import { Spinner } from "./ui/EmptyState";
import { createAppTheme } from "./theme/theme";

import LoginPage from "./pages/LoginPage";
import Home from "./pages/Home";
import Sales from "./pages/Sales";
import EbayListings from "./pages/EbayListings";
import Inventory from "./pages/Inventory";
import InventoryDetail from "./pages/InventoryDetail";
import Returns from "./pages/Returns";
import Shipments from "./pages/Shipments";
import SettingsPage from "./pages/SettingsPage";
import PokimanePage from "./pages/Pokimane";
import UnfulfilledSales from "./pages/UnfulfilledSales";
import Tasks from "./pages/Tasks";
import Upcoming from "./pages/Upcoming";
import ScheduledBids from "./pages/ScheduledBids";
import ManualUpdates from "./pages/ManualUpdates";
import MatchOrders from "./pages/MatchOrders";
import FBCandidates from "./pages/FBCandidates";
import FBQueue from "./pages/FBQueue";
import Subscriptions from "./pages/Subscriptions";
import FailedListings from "./pages/FailedListings";
import Funds from "./pages/Funds";
import Trash from "./pages/Trash";
import PnL from "./pages/PnL";
import Expenses from "./pages/Expenses";

import PublicCatalog from "./pages/PublicCatalog";
import PublicContact from "./pages/PublicContact";

import {
  Home as HomeIcon, Boxes, ShoppingCart, AlertTriangle, Wrench, Calendar, Clock,
  PackageOpen, Truck, ListChecks, Link2, Facebook, Settings as SettingsIcon, LogOut,
  DollarSign, Trash2,
} from "lucide-react";

export const ColorModeContext = createContext({ mode: "light", toggle: () => {} });
export function useColorMode() { return useContext(ColorModeContext); }

// Route-aware ErrorBoundary: when the user navigates, force-remount the
// boundary so a previous route's crash doesn't persist its error state into
// the new route. Cheap (just keys off pathname) and invisible until needed.
function RouteAwareErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

function AdminLayout() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const openPalette = useOpenCommandPalette();

  const commands = useMemo(
    () => [
      { id: "go-home",       group: "Go to", label: "Dashboard",        icon: <HomeIcon size={14} />,    run: () => navigate("/admint/") },
      { id: "go-inventory",  group: "Go to", label: "Inventory",        icon: <Boxes size={14} />,       run: () => navigate("/admint/inventory") },
      { id: "go-trash",      group: "Go to", label: "Trash candidates", icon: <Trash2 size={14} />,      run: () => navigate("/admint/trash") },
      { id: "go-sales",      group: "Go to", label: "Sales",            icon: <ShoppingCart size={14} />, run: () => navigate("/admint/sales") },
      { id: "go-funds",      group: "Go to", label: "Funds",            icon: <DollarSign size={14} />, run: () => navigate("/admint/funds") },
      { id: "go-pnl",        group: "Go to", label: "P&L statement",    icon: <DollarSign size={14} />, run: () => navigate("/admint/pnl") },
      { id: "go-expenses",   group: "Go to", label: "Expenses",         icon: <DollarSign size={14} />, run: () => navigate("/admint/expenses") },
      { id: "go-listings",   group: "Go to", label: "Listings",         icon: <ShoppingCart size={14} />, run: () => navigate("/admint/listings") },
      { id: "go-fb",         group: "Go to", label: "Facebook Candidates", icon: <Facebook size={14} />, run: () => navigate("/admint/fb-candidates") },
      { id: "go-fb-queue",   group: "Go to", label: "FB Queue",         icon: <Facebook size={14} />, run: () => navigate("/admint/fb-queue") },
      { id: "go-match",      group: "Go to", label: "Match Orders",     icon: <Link2 size={14} />,       run: () => navigate("/admint/match-orders") },
      { id: "go-unfulfilled",group: "Go to", label: "Unfulfilled Sales",icon: <AlertTriangle size={14} />, run: () => navigate("/admint/unfulfilled-sales") },
      { id: "go-manual",     group: "Go to", label: "Manual Updates",   icon: <Wrench size={14} />,      run: () => navigate("/admint/manual-updates") },
      { id: "go-failed",     group: "Go to", label: "Failed Listings",  icon: <AlertTriangle size={14} />, run: () => navigate("/admint/failed-listings") },
      { id: "go-shipments",  group: "Go to", label: "Shipments",        icon: <Truck size={14} />,       run: () => navigate("/admint/shipments") },
      { id: "go-returns",    group: "Go to", label: "Returns",          icon: <PackageOpen size={14} />, run: () => navigate("/admint/returns") },
      { id: "go-scheduled",  group: "Go to", label: "Scheduled Bids",   icon: <Calendar size={14} />,    run: () => navigate("/admint/scheduled-bids") },
      { id: "go-upcoming",   group: "Go to", label: "Upcoming Auctions",icon: <Clock size={14} />,       run: () => navigate("/admint/upcoming") },
      { id: "go-tasks",      group: "Go to", label: "Tasks",            icon: <ListChecks size={14} />,  run: () => navigate("/admint/tasks") },
      { id: "go-subs",       group: "Go to", label: "Subscriptions",    icon: <ListChecks size={14} />,  run: () => navigate("/admint/subscriptions") },
      { id: "go-settings",   group: "Account", label: "Settings",       icon: <SettingsIcon size={14} />, run: () => navigate("/admint/settings") },
      { id: "logout",        group: "Account", label: "Sign out",       icon: <LogOut size={14} />,      run: () => logout() },
    ],
    [navigate, logout]
  );

  return (
    <div className="min-h-screen flex">
      <SideNav onOpenPalette={openPalette} />
      <div
        className="flex-1 flex flex-col min-w-0"
        style={{ marginLeft: SIDENAV_DEFAULT_WIDTH }}
        data-collapse-aware
      >
        <TopBar onOpenPalette={openPalette} />
        <main className="flex-1 px-5 py-6 lg:px-8">
          <RouteAwareErrorBoundary>
            <Outlet />
          </RouteAwareErrorBoundary>
        </main>
      </div>
      <CommandPalette commands={commands} />
    </div>
  );
}

function RequireAuth({ children }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2 text-ink-muted text-sm">
          <Spinner size={16} />
          <span>Loading…</span>
        </div>
      </div>
    );
  }
  if (!authenticated) {
    return <Navigate to="/admint/login" replace state={{ from: location }} />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin =
    location.pathname === "/admint" || location.pathname.startsWith("/admint/");

  if (!isAdmin) {
    return (
      <RouteAwareErrorBoundary>
        <Routes>
          <Route path="/" element={<PublicCatalog />} />
          <Route path="/contact" element={<PublicContact />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouteAwareErrorBoundary>
    );
  }

  return (
    <Routes>
      <Route path="/admint/login" element={<LoginPage />} />
      <Route path="/admint" element={<Navigate to="/admint/" replace />} />
      <Route
        path="/admint/*"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="sales" element={<Sales />} />
        <Route path="funds" element={<Funds />} />
        <Route path="pnl" element={<PnL />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="listings" element={<EbayListings />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="trash" element={<Trash />} />
        <Route path="inventory/:key" element={<InventoryDetail />} />
        <Route path="match-orders" element={<MatchOrders />} />
        <Route path="fb-candidates" element={<FBCandidates />} />
        <Route path="fb-queue" element={<FBQueue />} />
        <Route path="unfulfilled-sales" element={<UnfulfilledSales />} />
        <Route path="manual-updates" element={<ManualUpdates />} />
        <Route path="failed-listings" element={<FailedListings />} />
        <Route path="upcoming" element={<Upcoming />} />
        <Route path="scheduled-bids" element={<ScheduledBids />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="returns" element={<Returns />} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="pokimane" element={<PokimanePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/admint/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  const [mode, setMode] = useState(() => localStorage.getItem("colorMode") || "light");
  const colorMode = useMemo(() => ({
    mode,
    toggle: () => setMode((m) => {
      const next = m === "light" ? "dark" : "light";
      localStorage.setItem("colorMode", next);
      return next;
    }),
  }), [mode]);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Tailwind dark variants and CSS-var palette swap key off `.dark` on <html>.
  // Sync it to MUI's mode so both styling systems flip together.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
